import * as React from 'react';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {classNames} from '@shopify/react-utilities/styles';
import {Button, Flex, Icon, Link, SocialLink, PoweredByShopify, Section} from 'components/UIKit';
import {SubscriptionLayout} from 'components/Marketplace';
import {FooterLink} from './types';
import {FooterList} from './components/FooterList';
import styles from './Footer.scss';
import * as locales from './locales';

interface FooterProps {
  authenticityToken: string;
  showSubscription: boolean;
  footerData: FooterData;
}

interface FooterData {
  primaryLinks: FooterLink[];
  resourceLinks: FooterLink[];
  businessTypeLinks: FooterLink[];
  locationLinks: FooterLink[];
  socialLinks: SocialLink[];
  cta: FooterLink;
}

function FooterComponent(props: FooterProps & WithI18nProps) {
  const {authenticityToken, showSubscription = true} = props;

  const {
    primaryLinks,
    resourceLinks,
    businessTypeLinks,
    locationLinks,
    socialLinks,
    cta,
  } = props.footerData;

  const {i18n} = props;

  return (
    <>
      {showSubscription && <SubscriptionLayout authenticityToken={authenticityToken} />}
      <Section
        heading={i18n.translate('heading')}
        headingProps={{hidden: true}}
        headerSpacing="none"
        spacing="small"
        component="footer"
        className={styles.Footer}
      >
        <div className={styles.Top}>
          <div className={styles.PrimaryLinks}>
            {primaryLinks.map((link) => (
              <Link
                key={link.url}
                url={link.url}
                text={link.label}
                type="h3"
                newTab={link.target === '_blank'}
                className={classNames(styles.PrimaryLink, styles.Link)}
                resetMargin
              />
            ))}
          </div>

          <Button url={cta.url} label={cta.label} light className={styles.Cta} />
        </div>
        <section className={styles.Body}>
          <FooterList
            label={i18n.translate('resources')}
            links={resourceLinks}
            className={styles.Resources}
          />
          <FooterList
            label={i18n.translate('businessTypes')}
            links={businessTypeLinks}
            className={styles.BusinessTypes}
          />
          <FooterList
            label={i18n.translate('locations')}
            links={locationLinks}
            className={styles.Locations}
          />
        </section>
        <Flex justifyContent="space-between" alignItems="center" className={styles.Bottom}>
          <Flex wrapperComponent="ul" className={styles.SocialList}>
            {socialLinks.map((platform) => (
              <li
                className={classNames(styles.SocialItem, styles[platform.name])}
                key={platform.name}
              >
                <a
                  className={styles.SocialLink}
                  href={platform.url}
                  target={platform.target}
                  rel="me noopener"
                  data-ga-event="Social"
                  data-ga-action={platform.name}
                  data-ga-label="Footer"
                  aria-label={i18n.translate(`social.${platform.name}`)}
                >
                  <Icon aria-hidden size={20} icon={platform.name} fill="white" />
                </a>
              </li>
            ))}
          </Flex>
          <PoweredByShopify lowlight />
        </Flex>
      </Section>
    </>
  );
}

export const Footer = withI18n({
  id: 'Footer',
  fallback: locales.en,
})(FooterComponent);
